import {createApi} from '@reduxjs/toolkit/query/react'
import type {ISuccessMessage} from '~/src/models'
import type {JobOrderWithinServiceOrderResponse} from '~/src/models/jobOrder'
import type {IOrder} from '~/src/models/order'
import type {
  IServiceOrder,
  PlatformAdminServiceOrderPagingResponse,
  TOrderPayload,
  TPagedServiceOrderPayload,
  TPositionOrderDistributionPayload,
  TTemplateOrderPayload,
} from '~/src/models/serviceOrder'
import {baseQuery} from '../base-query'

export const serviceOrderApi = createApi({
  reducerPath: 'ServiceOrderApi',
  tagTypes: ['ServiceOrder'],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getServiceOrder: builder.query<TOrderPayload, TPagedServiceOrderPayload>({
      providesTags: ['ServiceOrder'],
      query: ({
        pageNumber,
        pageSize,
        filterBy,
        agencyId,
        companyId,
        orderNumberSearch,
        requestDateFrom,
        requestDateTo,
        sortByStartDate,
      }) => ({
        url: `/api/serviceorder/getServiceOrders`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          filterBy,
          agencyId,
          ...(companyId && {companyId}),
          ...(orderNumberSearch && {orderNumberSearch}),
          ...(requestDateFrom && {requestDateFrom}),
          ...(requestDateTo && {requestDateTo}),
          ...(sortByStartDate && {sortByStartDate}),
        },
      }),
    }),

    getSingleServiceOrder: builder.query<IServiceOrder, {id: string}>({
      providesTags: ['ServiceOrder'],
      query: ({id}) => ({
        url: `/api/serviceorder/${id}`,
        method: 'GET',
      }),
    }),

    getServiceOrderTemplates: builder.query<TTemplateOrderPayload, TPagedServiceOrderPayload>({
      providesTags: ['ServiceOrder'],
      query: ({pageNumber, pageSize, agencyId}) => ({
        url: `/api/ServiceOrderTemplate/GetServiceOrderTemplates`,
        method: 'GET',
        params: {pageNumber, pageSize, agencyId},
      }),
    }),

    getServiceOrderTemplateById: builder.query<TOrderPayload, {id: string; agencyId?: string}>({
      providesTags: ['ServiceOrder'],
      query: ({id, agencyId}) => ({
        url: `/api/serviceordertemplate/getServiceOrderTemplate/${id}`,
        method: 'GET',
        params: {agencyId},
      }),
    }),

    getServiceOrdersByAgencyId: builder.query<TOrderPayload, TPagedServiceOrderPayload>({
      providesTags: ['ServiceOrder'],
      query: ({pageNumber, pageSize, agencyId}) => ({
        url: `/api/ServiceOrder/GetServiceOrdersByAgencyId`,
        method: 'GET',
        params: {pageNumber, pageSize, agencyId},
      }),
    }),

    /**
     * Get service orders by company ID with optional region/site filtering
     *
     * @param companyId - Required: The company to filter by
     * @param regionId - Optional: Filter orders by region (independent of siteId)
     * @param siteId - Optional: Filter orders by site (independent of regionId)
     * @param agencyId - Optional: Filter by agency
     * @param filterBy - Optional: Filter type (e.g., 'pending', 'active')
     * @param orderNumberSearch - Optional: Search by order number
     * @param requestDateFrom/To - Optional: Date range filter
     * @param sortByStartDate - Optional: Sort direction
     */
    getServiceOrderByCompanyId: builder.query<
      TOrderPayload,
      {
        companyId?: string
        pageNumber?: number
        pageSize?: number
        agencyId?: string
        filterBy?: string
        // Region and Site filtering (both optional and independent)
        regionId?: string
        siteId?: string
        // Search and date filter params
        orderNumberSearch?: string
        requestDateFrom?: string
        requestDateTo?: string
        sortByStartDate?: 'asc' | 'desc'
      }
    >({
      providesTags: ['ServiceOrder'],
      query: ({
        companyId,
        pageNumber = 1,
        pageSize = 10,
        agencyId,
        filterBy,
        regionId,
        siteId,
        orderNumberSearch,
        requestDateFrom,
        requestDateTo,
        sortByStartDate,
      }) => ({
        url: `/api/serviceorder/getServiceOrders`,
        method: 'GET',
        params: {
          companyId,
          pageNumber,
          pageSize,
          ...(agencyId && {agencyId}),
          ...(filterBy && {filterBy}),
          ...(regionId && {regionId}),
          ...(siteId && {siteId}),
          ...(orderNumberSearch && {orderNumberSearch}),
          ...(requestDateFrom && {requestDateFrom}),
          ...(requestDateTo && {requestDateTo}),
          ...(sortByStartDate && {sortByStartDate}),
        },
      }),
    }),

    createServiceOrder: builder.mutation<IServiceOrder, Partial<IServiceOrder>>({
      invalidatesTags: ['ServiceOrder'],
      query: (body) => ({
        url: `/api/serviceorder/createServiceOrder`,
        method: 'POST',
        body: body,
      }),
    }),

    createServiceOrderTemplate: builder.mutation<
      ISuccessMessage,
      {template: Partial<IServiceOrder>; agencyId?: string}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: ({template, agencyId}) => ({
        url: `/api/ServiceOrderTemplate/CreateServiceOrderTemplate`,
        method: 'POST',
        body: template,
        params: {agencyId},
      }),
    }),

    createServiceOrderFromTemplate: builder.mutation<
      ISuccessMessage,
      {template: Partial<IServiceOrder>; agencyId?: string}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: ({template, agencyId}) => ({
        url: `/api/ServiceOrder/CreateServiceOrderFromTemplate`,
        method: 'POST',
        body: template,
        params: {agencyId},
      }),
    }),

    updateServiceOrderTemplate: builder.mutation<
      ISuccessMessage,
      {template: IServiceOrder; agencyId?: string}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: ({template, agencyId}) => ({
        url: `/api/serviceordertemplate/updateServiceOrderTemplate`,
        method: 'PUT',
        body: template,
        params: {agencyId},
      }),
    }),

    updateServiceOrder: builder.mutation<IServiceOrder, IOrder[]>({
      invalidatesTags: ['ServiceOrder'],
      query: (body) => ({
        url: `/api/serviceorder`,
        method: 'PUT',
        body: body,
      }),
    }),

    updatePositionOrderDistribution: builder.mutation<
      ISuccessMessage,
      TPositionOrderDistributionPayload & {forceUpdate?: boolean}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: ({serviceOrderId, positionId, agencyId, body, forceUpdate = false}) => ({
        url: `/api/ServiceOrder/UpdatePositionOrderDistribution`,
        method: 'PUT',
        body: body,
        params: {serviceOrderId, positionId, agencyId, forceUpdate},
      }),
    }),

    updateMspStatusToPreparingPledges: builder.mutation<
      ISuccessMessage,
      {serviceOrderId: string; agencyId?: string}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: (params) => ({
        url: `/api/ServiceOrder/updateMspStatusToPreparingPledges`,
        method: 'PUT',
        params: params,
      }),
    }),

    updateMspStatusToConfiguringJobOrder: builder.mutation<
      ISuccessMessage,
      {serviceOrderId: string; agencyId?: string}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: (params) => ({
        url: `/api/serviceorder/updateMspStatusToConfiguringJobOrder`,
        method: 'PUT',
        params: params,
      }),
    }),

    updateMspStatusToPledgesReceived: builder.mutation<
      ISuccessMessage,
      {serviceOrderId: string; agencyId?: string}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: (params) => ({
        url: `/api/serviceorder/updateMspStatusToPledgesReceived`,
        method: 'PUT',
        params: params,
      }),
    }),

    updateAgencyStatusToConfirming: builder.mutation<
      ISuccessMessage,
      {serviceOrderId: string; agencyId?: string}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: (params) => ({
        url: `/api/serviceorder/updateAgencyStatusToConfirming`,
        method: 'PUT',
        params: params,
      }),
    }),

    submitServiceOrder: builder.mutation<ISuccessMessage, {serviceOrderId: string}>({
      invalidatesTags: ['ServiceOrder'],
      query: ({serviceOrderId}) => ({
        url: `/api/ServiceOrder/SubmitServiceOrder`,
        method: 'PUT',
        params: {serviceOrderId},
      }),
    }),

    pledgeAgencyOrder: builder.mutation<
      ISuccessMessage,
      {serviceOrderId: string; agencyId?: string}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: (params) => ({
        url: `/api/ServiceOrder/PledgeAgencyOrder`,
        method: 'PUT',
        params: params,
      }),
    }),

    acceptServiceOrder: builder.mutation<ISuccessMessage, IServiceOrder>({
      invalidatesTags: ['ServiceOrder'],
      query: ({serviceOrderId}) => ({
        url: `/api/ServiceOrder/AcceptServiceOrder`,
        method: 'PUT',
        params: {serviceOrderId},
      }),
    }),

    distributeServiceOrder: builder.mutation<
      ISuccessMessage,
      {serviceOrderId: string; agencyId?: string}
    >({
      invalidatesTags: ['ServiceOrder'],
      query: (params) => ({
        url: `/api/ServiceOrder/DistributeServiceOrder`,
        method: 'PUT',
        params: params,
      }),
    }),

    // New Platform Admin endpoints
    getPlatformAdminServiceOrders: builder.query<
      PlatformAdminServiceOrderPagingResponse,
      {pageNumber?: number; pageSize?: number; agencyId?: string}
    >({
      providesTags: ['ServiceOrder'],
      query: ({pageNumber = 1, pageSize = 10, agencyId}) => ({
        url: `/api/serviceorder/platform-admin`,
        method: 'GET',
        params: {pageNumber, pageSize, ...(agencyId && {agencyId})},
      }),
    }),

    getJobOrdersWithinServiceOrder: builder.query<
      JobOrderWithinServiceOrderResponse[],
      {serviceOrderId: string}
    >({
      providesTags: ['ServiceOrder'],
      query: ({serviceOrderId}) => ({
        url: `/api/serviceorder/${serviceOrderId}/job-orders`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetServiceOrderQuery,
  useGetServiceOrderTemplatesQuery,
  useGetServiceOrderTemplateByIdQuery,
  useGetSingleServiceOrderQuery,
  useLazyGetSingleServiceOrderQuery,
  useCreateServiceOrderFromTemplateMutation,
  useCreateServiceOrderTemplateMutation,
  useUpdateServiceOrderTemplateMutation,
  useLazyGetServiceOrderQuery,
  useGetServiceOrdersByAgencyIdQuery,
  useGetServiceOrderByCompanyIdQuery,
  useCreateServiceOrderMutation,
  useSubmitServiceOrderMutation,
  useDistributeServiceOrderMutation,
  usePledgeAgencyOrderMutation,
  useAcceptServiceOrderMutation,
  useUpdateServiceOrderMutation,
  useUpdatePositionOrderDistributionMutation,
  useUpdateMspStatusToPreparingPledgesMutation,
  useUpdateMspStatusToConfiguringJobOrderMutation,
  useUpdateMspStatusToPledgesReceivedMutation,
  useUpdateAgencyStatusToConfirmingMutation,
  // New Platform Admin hooks
  useGetPlatformAdminServiceOrdersQuery,
  useGetJobOrdersWithinServiceOrderQuery,
} = serviceOrderApi
